import React, { useState } from 'react';
import styled from 'styled-components';
import { FaEtsy, FaInstagram, FaFacebook } from 'react-icons/fa';

// ---------- STYLED COMPONENTS ----------

// Main page container (background gradient)
const CustomOrderSection = styled.section`
  min-height: 100vh;
  padding: 6rem 2rem 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(
    135deg,
    #FFCDE7 0%,
    #F3E3F7 40%,
    #FFF5FC 100%
  );
`;

// Title in Dancing Script
const Title = styled.h1`
  color: #7A4988;
  font-family: 'Dancing Script', cursive;
  font-size: clamp(2rem, 4vw, 3rem);
  margin-bottom: 1rem;
  text-align: center;
`;

// Instructions text
const Instructions = styled.p`
  color: #5a5a5a;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 2.5rem;
  font-size: 1.1rem;
  font-family: 'Poppins', sans-serif;
`;

// Card-like wrapper for the form
const FormWrapper = styled.div`
  background-color: #fff;
  width: 100%;
  max-width: 650px;
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 8px 30px rgba(122, 73, 136, 0.15);
`;

// Actual form
const Form = styled.form`
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', sans-serif;
`;

// Label
const Label = styled.label`
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #7A4988;
`;

// Inputs
const Input = styled.input`
  padding: 0.6rem;
  border: 1px solid #ccc;
  border-radius: 0.5rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  margin-bottom: 1rem;
  outline: none;

  &:focus {
    border-color: #FFAAD7;
    box-shadow: 0 0 0 2px rgba(255, 170, 215, 0.2);
  }
`;

// TextArea
const TextArea = styled.textarea`
  padding: 0.6rem;
  border: 1px solid #ccc;
  border-radius: 0.5rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  margin-bottom: 1rem;
  outline: none;

  &:focus {
    border-color: #FFAAD7;
    box-shadow: 0 0 0 2px rgba(255, 170, 215, 0.2);
  }
`;

// Submit button
const SubmitButton = styled.button`
  background-color: #FFAAD7;
  color: #fff;
  font-size: 1rem;
  padding: 0.8rem 1.6rem;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
  box-shadow: 0 4px 12px rgba(255, 170, 215, 0.4);
  margin: 1rem auto 0 auto;
  opacity: ${(p) => (p.disabled ? 0.75 : 1)};

  &:hover {
    background-color: ${(p) => (p.disabled ? '#FFAAD7' : '#FF85C0')};
    transform: ${(p) => (p.disabled ? 'none' : 'translateY(-2px)')};
  }

  &:active {
    transform: translateY(0);
  }
`;

// Status message
const StatusMessage = styled.p`
  margin-top: 1rem;
  text-align: center;
  font-family: 'Poppins', sans-serif;
  font-size: 0.98rem;
  color: ${(p) => (p.$type === 'success' ? '#2e7d32' : '#c62828')};
`;

// ---------- FOOTER STYLES ----------
const Footer = styled.footer`
  padding: 3rem 2rem 1.5rem;
  background-color: #7A4988;
  color: #fff;
  font-family: 'Poppins', sans-serif;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1.2rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 30px;
    height: 2px;
    background-color: #FFAAD7;
  }
`;

const FooterLink = styled.a`
  color: #F3E3F7;
  text-decoration: none;
  margin-bottom: 0.8rem;
  transition: all 0.3s ease;

  &:hover {
    color: #FFAAD7;
    transform: translateX(3px);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const SocialIcon = styled.a`
  color: #F3E3F7;
  font-size: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    color: #FFAAD7;
    transform: translateY(-3px);
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
`;

// ---------- COMPONENT ----------
function CustomOrder() {
  // State for form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [requestDetails, setRequestDetails] = useState('');
  const [dateNeeded, setDateNeeded] = useState('');

  // UX state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });


  const FUNCTION_URL =
    'https://us-central1-lav-and-palm-co.cloudfunctions.net/customOrder';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          dateNeeded,
          requestDetails,
          website: '', // honeypot field
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to send. Please try again.');

      setStatus({ type: 'success', message: 'Sent! We’ll get back to you soon 💜' });

      // Clear form on success
      setName('');
      setEmail('');
      setPhone('');
      setRequestDetails('');
      setDateNeeded('');
    } catch (err) {
      setStatus({
        type: 'error',
        message: err?.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Main Section */}
      <CustomOrderSection>
        <Title>Custom Order Request</Title>
        <Instructions>
          Please fill out the form with your specific request, and your contact info.
          Custom order processing time is currently one to two weeks. Thanks for stopping by!
        </Instructions>

        <FormWrapper>
          <Form onSubmit={handleSubmit}>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isSubmitting}
            />

            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
            />

            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              disabled={isSubmitting}
            />

            <Label htmlFor="dateNeeded">Date Needed By</Label>
            <Input
              id="dateNeeded"
              type="date"
              value={dateNeeded}
              onChange={(e) => setDateNeeded(e.target.value)}
              required
              disabled={isSubmitting}
            />

            <Label htmlFor="requestDetails">Request Details</Label>
            <TextArea
              id="requestDetails"
              value={requestDetails}
              onChange={(e) => setRequestDetails(e.target.value)}
              required
              disabled={isSubmitting}
            />

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Submit'}
            </SubmitButton>

            {status.message && (
              <StatusMessage $type={status.type}>{status.message}</StatusMessage>
            )}
          </Form>
        </FormWrapper>
      </CustomOrderSection>

      {/* Footer */}
      <Footer>
        <FooterContent>
          <FooterSection>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLink href="/">Home</FooterLink>
            <FooterLink href="/custom-order">Custom Orders</FooterLink>
            <FooterLink href="/faq">FAQ</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Policies</FooterTitle>
            <FooterLink href="/shipping">Shipping</FooterLink>
            <FooterLink href="/returns">Returns</FooterLink>
            <FooterLink href="/privacy">Privacy Policy</FooterLink>
            <FooterLink href="/terms">Terms of Service</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Connect With Us</FooterTitle>
            <p>Follow us for updates and behind-the-scenes content!</p>
            <SocialLinks>
              <SocialIcon href="https://www.instagram.com/lavenderandpalmcrochet/">
                <FaInstagram />
              </SocialIcon>
              <SocialIcon href="https://www.facebook.com/people/Lavender-and-Palm-Crochet/61566023416208/#">
                <FaFacebook />
              </SocialIcon>
              <SocialIcon href="https://lavenderandpalm.etsy.com">
                <FaEtsy />
              </SocialIcon>
            </SocialLinks>
          </FooterSection>
        </FooterContent>

        <Copyright>
          © {new Date().getFullYear()} Lavender & Palm Co. All rights reserved.
        </Copyright>
      </Footer>
    </>
  );
}

export default CustomOrder;